/**
 * The notification demonstrator, as it was in main.ts.
 *
 * Behaviour is unchanged from the vanilla version that was proven on iOS; only the rendering moved
 * into React. One incidental improvement: the event log now renders as text rather than being
 * assembled with innerHTML, so a server error string can no longer carry markup into the page.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  blockingReason,
  describeEnvironment,
  registerServiceWorker,
  requestPermission,
  showNotification,
  type EnvironmentReport,
} from "@/features/notifications/notifications";
import { currentSubscription, pushSupported, sendTestPush, subscribeToPush, unsubscribeFromPush } from "@/features/notifications/push";
import { useAuth } from "@/app/AuthContext";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

const NOTIFICATION_INTERVAL_MS = 5 * 60 * 1000;
const COUNTDOWN_TICK_MS = 1000;
const MAX_LOG_ENTRIES = 50;

/** Only an admin may fan a push out to every subscribed device, so the button says so up front. */
const ADMIN_ONLY_ROLE = "push_send_test";

interface LogEntry {
  id: number;
  time: string;
  message: string;
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function NotificationsPanel() {
  const { user } = useAuth();

  const [env, setEnv] = useState<EnvironmentReport>(() => describeEnvironment());
  const [blocked, setBlocked] = useState<string | null>(null);
  const [status, setStatus] = useState("Checking this device…");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const [subscribed, setSubscribed] = useState(false);
  const [pushStatus, setPushStatus] = useState<string | null>(null);

  const sentCount = useRef(0);
  const nextNotificationAt = useRef<number | undefined>(undefined);
  const heartbeatTimer = useRef<number | undefined>(undefined);
  // Effects run twice under StrictMode in development; without this the page would greet you with
  // two notifications and two log histories.
  const initialised = useRef(false);

  const addLog = useCallback((message: string): void => {
    setLog((entries) =>
      [{ id: Date.now() + Math.random(), time: new Date().toLocaleTimeString(), message }, ...entries].slice(
        0,
        MAX_LOG_ENTRIES,
      ),
    );
  }, []);

  const notify = useCallback(
    async (trigger: string): Promise<void> => {
      sentCount.current += 1;
      try {
        await showNotification(
          "Biopod v2 test notification",
          `Notification #${sentCount.current} (${trigger}) at ${new Date().toLocaleTimeString()}`,
        );
        addLog(`Sent notification #${sentCount.current} (${trigger}).`);
      } catch (error) {
        addLog(`Failed to send notification: ${describeError(error)}`);
      }
    },
    [addLog],
  );

  const startHeartbeat = useCallback((): void => {
    if (heartbeatTimer.current !== undefined) return;

    nextNotificationAt.current = Date.now() + NOTIFICATION_INTERVAL_MS;
    heartbeatTimer.current = window.setInterval(() => {
      nextNotificationAt.current = Date.now() + NOTIFICATION_INTERVAL_MS;
      void notify("5 min heartbeat");
    }, NOTIFICATION_INTERVAL_MS);

    addLog(`Heartbeat started: one notification every ${NOTIFICATION_INTERVAL_MS / 60_000} minutes.`);
    addLog("Timers are throttled or frozen while the page is backgrounded; keep it in the foreground to test.");
  }, [addLog, notify]);

  const refreshPushState = useCallback(async (): Promise<void> => {
    if (!pushSupported()) {
      setPushStatus("This browser does not support the Push API.");
      return;
    }
    const existing = await currentSubscription();
    setSubscribed(existing !== null);
    setPushStatus(
      existing !== null
        ? "Subscribed. Close the app completely, then send a push from another device."
        : "Not subscribed to background push.",
    );
  }, []);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    void (async () => {
      const report = describeEnvironment();
      setEnv(report);

      const reason = blockingReason(report);
      if (reason !== null) {
        setBlocked(reason);
        setStatus("Notifications are unavailable on this device or origin.");
        addLog(`Blocked: ${reason}`);
        return;
      }

      // A previously granted permission survives reloads, so resume without a tap.
      if (report.permission === "granted") {
        await registerServiceWorker();
        setEnabled(true);
        setStatus("Notifications already enabled.");
        addLog("Permission was already granted; resuming.");
        await notify("page load");
        startHeartbeat();
        await refreshPushState();
        return;
      }

      setStatus("Tap “Enable notifications” to grant permission.");
    })();
  }, [addLog, notify, refreshPushState, startHeartbeat]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setRemainingMs(nextNotificationAt.current === undefined ? null : nextNotificationAt.current - Date.now());
    }, COUNTDOWN_TICK_MS);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => () => window.clearInterval(heartbeatTimer.current), []);

  const handleEnable = async (): Promise<void> => {
    setBusy(true);
    const permission = await requestPermission();
    addLog(`Permission request returned "${permission}".`);
    setEnv(describeEnvironment());

    if (permission !== "granted") {
      setBusy(false);
      setStatus(
        permission === "denied"
          ? "Permission denied. Re-enable notifications for this site in your browser settings."
          : "Permission was dismissed. Tap the button again to retry.",
      );
      return;
    }

    await registerServiceWorker();
    addLog("Service worker registered and active.");
    setEnabled(true);
    setStatus("Notifications enabled.");
    setBusy(false);

    await notify("initial");
    startHeartbeat();
    await refreshPushState();
  };

  const runPushAction = async (action: () => Promise<string>): Promise<void> => {
    setBusy(true);
    try {
      addLog(await action());
    } catch (error) {
      addLog(describeError(error));
    } finally {
      setBusy(false);
      await refreshPushState();
    }
  };

  const maySendTest = user?.roles.includes(ADMIN_ONLY_ROLE) ?? false;
  const countdown =
    remainingMs === null
      ? "not scheduled"
      : `${Math.floor(Math.max(0, Math.ceil(remainingMs / 1000)) / 60)}:${String(
          Math.max(0, Math.ceil(remainingMs / 1000)) % 60,
        ).padStart(2, "0")}`;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-5">
        {blocked !== null && (
          <p role="alert" className="rounded-lg border border-amber-500 bg-warning-bg p-4 text-sm text-amber-700">
            {blocked}
          </p>
        )}

        <Section title="Notifications">
          <p className="text-sm text-text-secondary">{status}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {!enabled && (
              <Button onClick={() => void handleEnable()} disabled={busy || blocked !== null} variant="primary">
                Enable notifications
              </Button>
            )}
            {enabled && <Button onClick={() => void notify("manual")}>Send a test notification now</Button>}
          </div>
          <p className="mt-4 text-sm text-text-muted">
            Next scheduled notification in <span className="tabular-nums text-text-secondary">{countdown}</span>
          </p>
        </Section>

        {enabled && (
          <Section title="Background push">
            <p className="text-sm text-text-secondary">{pushStatus ?? "Checking…"}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {!subscribed && (
                <Button
                  onClick={() =>
                    void runPushAction(async () => {
                      await subscribeToPush();
                      return "Subscribed to background push.";
                    })
                  }
                  disabled={busy}
                  variant="primary"
                >
                  Enable background push
                </Button>
              )}
              {subscribed && (
                <>
                  <Button
                    onClick={() =>
                      void runPushAction(async () => {
                        const result = await sendTestPush();
                        return `Push requested: sent=${result.sent} failed=${result.failed} pruned=${result.pruned}.`;
                      })
                    }
                    disabled={busy || !maySendTest}
                    title={maySendTest ? undefined : "Only an admin can send to every subscribed device"}
                  >
                    Send a push (works with the app closed)
                  </Button>
                  <Button
                    onClick={() =>
                      void runPushAction(async () => {
                        await unsubscribeFromPush();
                        return "Unsubscribed from background push.";
                      })
                    }
                    disabled={busy}
                  >
                    Disable background push
                  </Button>
                </>
              )}
            </div>
            {subscribed && !maySendTest && (
              <p className="mt-3 text-xs text-text-muted">
                Sending a test push notifies every subscribed device, so it is restricted to admins.
              </p>
            )}
          </Section>
        )}

        <Section title="Environment">
          <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm">
            <Row label="Secure context (HTTPS)" value={env.secureContext} />
            <Row label="Notifications API" value={env.notificationApi} />
            <Row label="Service Worker API" value={env.serviceWorkerApi} />
            <Row label="Installed / standalone" value={env.standalone} />
            <Row label="iOS device" value={env.ios} />
            <Row label="Permission" value={env.permission} />
          </dl>
        </Section>

        <Section title="Event log">
          {log.length === 0 ? (
            <p className="text-sm text-text-muted">Nothing yet.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {log.map((entry) => (
                <li key={entry.id} className="flex gap-3">
                  <time className="shrink-0 tabular-nums text-text-muted">{entry.time}</time>
                  <span className="text-text-secondary">{entry.message}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
    </div>
  );
}

/** A titled card. Local to this screen until a second screen wants the same treatment. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">{title}</h2>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: boolean | string }) {
  const ok = typeof value === "boolean" ? value : value === "granted";
  const bad = typeof value === "boolean" ? !value : value === "denied";
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className={ok ? "text-ok" : bad ? "text-danger" : "text-warning"}>
        {typeof value === "boolean" ? (value ? "yes" : "no") : value}
      </dd>
    </>
  );
}
